<template>
  <v-dialog v-model="isOpen" max-width="500" persistent>
    <v-card>
      <v-card-title class="text-h5 bg-primary text-white pa-4">Set New Password</v-card-title>

      <v-card-text class="pt-6 pb-4">
        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
          You're signing in with a temporary password. Please set a new permanent password to
          continue.
        </v-alert>

        <v-form ref="formRef" @submit.prevent="handleSubmit">
          <!-- New Password -->
          <v-text-field
            v-model="newPassword"
            :type="showPassword ? 'text' : 'password'"
            label="New Password *"
            placeholder="Enter your new password"
            :rules="passwordRules"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPassword = !showPassword"
            variant="outlined"
            density="comfortable"
            class="mb-4"
            hint="At least 8 characters, including uppercase, lowercase, number, and special character"
            persistent-hint
          ></v-text-field>

          <!-- Confirm Password -->
          <v-text-field
            v-model="confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            label="Confirm Password *"
            placeholder="Re-enter your new password"
            :rules="confirmPasswordRules"
            :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showConfirmPassword = !showConfirmPassword"
            variant="outlined"
            density="comfortable"
            class="mb-2"
          ></v-text-field>

          <!-- Password Requirements -->
          <v-card variant="outlined" class="pa-3 mb-4">
            <div class="text-caption text-grey-darken-1 mb-2">Password must contain:</div>
            <div class="d-flex flex-column" style="gap: 4px">
              <div class="d-flex align-center" style="gap: 8px">
                <v-icon
                  :color="hasMinLength ? 'success' : 'grey'"
                  size="small"
                  :icon="hasMinLength ? 'mdi-check-circle' : 'mdi-circle-outline'"
                ></v-icon>
                <span class="text-caption">At least 8 characters</span>
              </div>
              <div class="d-flex align-center" style="gap: 8px">
                <v-icon
                  :color="hasUppercase ? 'success' : 'grey'"
                  size="small"
                  :icon="hasUppercase ? 'mdi-check-circle' : 'mdi-circle-outline'"
                ></v-icon>
                <span class="text-caption">One uppercase letter</span>
              </div>
              <div class="d-flex align-center" style="gap: 8px">
                <v-icon
                  :color="hasLowercase ? 'success' : 'grey'"
                  size="small"
                  :icon="hasLowercase ? 'mdi-check-circle' : 'mdi-circle-outline'"
                ></v-icon>
                <span class="text-caption">One lowercase letter</span>
              </div>
              <div class="d-flex align-center" style="gap: 8px">
                <v-icon
                  :color="hasNumber ? 'success' : 'grey'"
                  size="small"
                  :icon="hasNumber ? 'mdi-check-circle' : 'mdi-circle-outline'"
                ></v-icon>
                <span class="text-caption">One number</span>
              </div>
              <div class="d-flex align-center" style="gap: 8px">
                <v-icon
                  :color="hasSpecialChar ? 'success' : 'grey'"
                  size="small"
                  :icon="hasSpecialChar ? 'mdi-check-circle' : 'mdi-circle-outline'"
                ></v-icon>
                <span class="text-caption">One special character (!@#$%^&*)</span>
              </div>
            </div>
          </v-card>

          <!-- Error Message -->
          <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mb-4">
            {{ errorMessage }}
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="handleCancel" :disabled="loading">Cancel</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="handleSubmit"
          :loading="loading"
          :disabled="!isFormValid"
        >
          Set Password
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

// Props
const props = defineProps<{
  modelValue: boolean
}>()

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [password: string]
  cancel: []
}>()

// Local state
const formRef = ref()
const newPassword = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const loading = ref(false)
const errorMessage = ref('')

// Computed
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// Password strength checks
const hasMinLength = computed(() => newPassword.value.length >= 8)
const hasUppercase = computed(() => /[A-Z]/.test(newPassword.value))
const hasLowercase = computed(() => /[a-z]/.test(newPassword.value))
const hasNumber = computed(() => /[0-9]/.test(newPassword.value))
const hasSpecialChar = computed(() => /[!@#$%^&*(),.?":{}|<>]/.test(newPassword.value))

const allRequirementsMet = computed(
  () =>
    hasMinLength.value &&
    hasUppercase.value &&
    hasLowercase.value &&
    hasNumber.value &&
    hasSpecialChar.value
)

// Validation rules
const passwordRules = [
  (v: string) => !!v || 'Password is required',
  (v: string) => v.length >= 8 || 'Password must be at least 8 characters',
  (v: string) => /[A-Z]/.test(v) || 'Password must contain an uppercase letter',
  (v: string) => /[a-z]/.test(v) || 'Password must contain a lowercase letter',
  (v: string) => /[0-9]/.test(v) || 'Password must contain a number',
  (v: string) => /[!@#$%^&*(),.?":{}|<>]/.test(v) || 'Password must contain a special character',
]

const confirmPasswordRules = [
  (v: string) => !!v || 'Please confirm your password',
  (v: string) => v === newPassword.value || 'Passwords do not match',
]

const isFormValid = computed(() => {
  return (
    newPassword.value &&
    confirmPassword.value &&
    newPassword.value === confirmPassword.value &&
    allRequirementsMet.value
  )
})

// Methods
async function handleSubmit() {
  const { valid } = await formRef.value.validate()
  if (!valid) return

  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'Passwords do not match'
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    emit('confirm', newPassword.value)
  } catch (error: any) {
    errorMessage.value = error.message || 'Failed to set password'
    loading.value = false
  }
}

function handleCancel() {
  emit('cancel')
  resetForm()
}

function resetForm() {
  newPassword.value = ''
  confirmPassword.value = ''
  showPassword.value = false
  showConfirmPassword.value = false
  errorMessage.value = ''
  loading.value = false
  formRef.value?.resetValidation()
}

// Watch for dialog close to reset form
watch(isOpen, (value) => {
  if (!value) {
    resetForm()
  }
})

// Expose methods for parent component
defineExpose({
  setLoading: (value: boolean) => {
    loading.value = value
  },
  setError: (message: string) => {
    errorMessage.value = message
    loading.value = false
  },
})
</script>

<style scoped>
:deep(.v-field) {
  border-radius: 8px;
}
</style>
